import React from 'react';

import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
  return (
    <div className="page">
      <Helmet>
        Privacy Policy - Homebrew Game Labs
      </Helmet>
      <header>
        <div className="logo">Homebrew <span>Labs</span></div>
        <h1>Privacy Policy</h1>
        <div className="effective-date">Effective Date: February 22, 2025</div>
      </header>

      <p>Welcome to <strong>Homebrew Game Labs</strong>. We take your privacy seriously. This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information. By using Homebrew Game Labs, you agree to the practices described here.</p>

      <h2>1. Who We Are</h2>
      <p>Homebrew Game Labs is a personal D&D campaign and character tracking application. It is intended for tabletop RPG players who want to manage their characters, campaigns, and game data in one place.</p>

      <h2>2. Information We Collect</h2>
      <p>We collect only what is necessary to provide the service:</p>
      <ul>
        <li><strong>Account information:</strong> When you register or log in (including via Facebook), we receive basic profile information such as your name and email address from your identity provider.</li>
        <li><strong>App content:</strong> D&D character data, campaign notes, and other content you create within the app.</li>
        <li><strong>Authentication data:</strong> Managed securely through AWS Cognito.</li>
        <li><strong>Usage data:</strong> Basic technical information (e.g., browser type, general error logs) used to keep the app running smoothly.</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>Your information is used solely to provide and improve the Homebrew Game Labs experience:</p>
      <ul>
        <li>To create and manage your account</li>
        <li>To store and display your D&D characters and campaign data</li>
        <li>To authenticate you securely</li>
        <li>To diagnose technical issues and improve app stability</li>
      </ul>
      <p>We do <strong>not</strong> sell, rent, or share your personal information with third parties for marketing or advertising purposes.</p>

      <h2>4. Third-Party Services</h2>
      <p>Homebrew Game Labs uses the following third-party services to operate:</p>
      <ul>
        <li><strong>AWS Cognito</strong> – for authentication and user account management. Governed by the <a href="https://aws.amazon.com/privacy/" target="_blank">AWS Privacy Policy</a>.</li>
        <li><strong>Facebook Login</strong> – as an optional sign-in method. If you use this option, Facebook may collect data per the <a href="https://www.facebook.com/privacy/policy/" target="_blank">Meta Privacy Policy</a>. We only receive the basic profile data needed to create your account.</li>
      </ul>
      <p>We do not control and are not responsible for the privacy practices of these third-party services.</p>

      <h2>5. Data Retention</h2>
      <p>We retain your account and app data for as long as your account is active. You may request deletion of your account and associated data at any time by contacting us (see Section 8). Upon deletion, your personal information and app content will be removed from our systems within a reasonable timeframe.</p>

      <h2>6. Data Security</h2>
      <p>We use industry-standard practices to protect your information, including secure authentication via AWS Cognito and encrypted data transmission. While we take reasonable precautions, no system is completely immune to security risks.</p>

      <h2>7. Children's Privacy</h2>
      <p>Homebrew Game Labs is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will promptly delete it.</p>

      <h2>8. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your account and associated data</li>
        <li>Withdraw consent for Facebook Login at any time via your Facebook app settings</li>
      </ul>
      <p>To exercise any of these rights, please contact us using the information below.</p>

      <h2>9. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. If we make significant changes, we will notify you by updating the effective date at the top of this page. Continued use of the app after changes constitutes your acceptance of the updated policy.</p>

      <h2>10. Contact Us</h2>
      <p>If you have any questions or concerns about this Privacy Policy or your data, please contact us at:</p>
      <p><strong>Homebrew Game Labs</strong><br />
        <a href="mailto:privacy@hbgamelabs.com">privacy@homebrewlabs.app</a></p>

      <footer>
        &copy; 2025 Homebrew Game Labs. All rights reserved.
      </footer>
    </div>
  );
}
